import { DateTime, DateTimeUnit } from 'luxon';
import { action, computed, makeObservable } from 'mobx';

import { ValueModel } from '@/models/ValueModel';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { TimesheetData, WeeklyWorkLog } from '../../types';

type TaskKeysFilterDependencies = {
  getTimesheetData: () => TimesheetData | null;
  getCurrentDate: () => DateTime;
  getView: () => string;
  setFilterData: (data: TimesheetData | null) => void;
};

export class TaskKeysFilterModel {
  private readonly _availableKeys = new ValueModel<string[]>([]);
  private readonly _selectedKeys = new ValueModel<string[]>([]);
  private _isInitialized = false;
  private readonly _dependencies: TaskKeysFilterDependencies;

  constructor(dependencies: TaskKeysFilterDependencies) {
    this._dependencies = dependencies;

    makeObservable(this, {
      availableKeys: computed,
      selectedKeys: computed,
      setAvailableKeys: action.bound,
      setSelectedKeys: action.bound,
      apply: action.bound,
      reset: action.bound,
    });
  }

  get availableKeys() {
    return this._availableKeys.value;
  }

  get selectedKeys() {
    return this._selectedKeys.value;
  }

  setAvailableKeys(keys: string[]) {
    const wasEmpty = this._availableKeys.value.length === 0;
    this._availableKeys.change(keys);

    if (wasEmpty && keys.length > 0 && !this._isInitialized) {
      this._selectedKeys.change([]);
      this._isInitialized = true;
    }
  }

  setSelectedKeys(keys: string[]) {
    const keysWithQueues = new Set<string>();

    keys.forEach((key) => {
      const queue = this.extractQueueFromTaskKey(key);

      if (queue) {
        keysWithQueues.add(queue);
      } else {
        if (!/^[A-Z]+-\d+/.test(key)) {
          keysWithQueues.add(key);
        }
      }
    });

    this._selectedKeys.change(Array.from(keysWithQueues));
  }

  apply() {
    const taskKeys = this._selectedKeys.value;
    const timesheetData = this._dependencies.getTimesheetData();

    if (!timesheetData) {
      this._dependencies.setFilterData(null);

      return;
    }

    if (taskKeys.length === 0) {
      this._dependencies.setFilterData(timesheetData);

      return;
    }

    const filteredDays: Record<string, WeeklyWorkLog[]> = {};
    let totalMinutes = 0;

    const view = this._dependencies.getView();
    const startDateMonth = this._dependencies.getCurrentDate().startOf(view as DateTimeUnit);

    Object.entries(timesheetData.days).forEach(([date, logs]) => {
      const filteredLogs = logs.filter((log) => {
        return taskKeys.some((key) => log.taskKey.startsWith(key));
      });

      filteredDays[date] = filteredLogs;

      filteredLogs.forEach((log) => {
        const logDT = DateTime.fromISO(log.start);

        if (view === 'week' || logDT.month === startDateMonth.month) {
          totalMinutes += log.durationMinutes;
        }
      });
    });

    const totalFormatted = formatDurationRussian(totalMinutes);

    this._dependencies.setFilterData({
      weekStart: timesheetData.weekStart,
      weekEnd: timesheetData.weekEnd,
      days: filteredDays,
      totalMinutes,
      totalFormatted,
    });
  }

  reset() {
    const timesheetData = this._dependencies.getTimesheetData();

    this._dependencies.setFilterData(timesheetData);
  }

  extractQueueFromTaskKey(taskKey: string): string | null {
    const match = taskKey.match(/^([A-Z]+)-\d+/);

    return match ? match[1] : null;
  }

  addQueueFromTaskKey(taskKey: string) {
    const queue = this.extractQueueFromTaskKey(taskKey);

    if (!queue) return;

    const currentAvailable = new Set(this._availableKeys.value);

    if (!currentAvailable.has(queue)) {
      currentAvailable.add(queue);
      const sortedQueues = Array.from(currentAvailable).sort();
      this._availableKeys.change(sortedQueues);
    }

    if (this._selectedKeys.value.length > 0) {
      const currentSelected = new Set(this._selectedKeys.value);

      if (!currentSelected.has(queue)) {
        currentSelected.add(queue);
        this._selectedKeys.change(Array.from(currentSelected));
      }
    }
  }

  removeQueueIfNotExists(queue: string) {
    const timesheetData = this._dependencies.getTimesheetData();

    if (!timesheetData) return;

    const hasQueueInData = Object.values(timesheetData.days).some((logs) =>
      logs.some((log) => {
        const logQueue = this.extractQueueFromTaskKey(log.taskKey);

        return logQueue === queue;
      }),
    );

    if (!hasQueueInData) {
      const currentAvailable = this._availableKeys.value.filter((key) => key !== queue);
      const currentSelected = this._selectedKeys.value.filter((key) => key !== queue);

      this._availableKeys.change(currentAvailable);
      this._selectedKeys.change(currentSelected);

      if (currentSelected.length === 0) {
        this._dependencies.setFilterData(timesheetData);
      }
    }
  }
}
